import { supabase } from '@/lib/supabase/client';
import type { DisplayType } from '@/types/display-types';

export const customDisplayTypesService = {
  async getAll(): Promise<DisplayType[]> {
    try {
      const { data, error } = await supabase.from('custom_display_types').select('*').order('name', { ascending: true });

      if (error) {
        console.error('Error fetching custom display types:', error);
        return [];
      }

      // Map custom_display_types to DisplayType format
      return (
        data?.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          size: item.size,
          category: item.category,
          selling_price: item.selling_price,
          created_at: item.created_at,
          updated_at: item.updated_at,
        })) || []
      );
    } catch (error) {
      console.error('Error fetching custom display types:', error);
      return [];
    }
  },

  async getById(id: string): Promise<DisplayType | null> {
    try {
      const { data, error } = await supabase.from('custom_display_types').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching custom display type:', error);
        return null;
      }

      if (!data) return null;

      // Map to DisplayType format
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        size: data.size,
        category: data.category,
        selling_price: data.selling_price,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
    } catch (error) {
      console.error('Error fetching custom display type:', error);
      return null;
    }
  },

  async create(data: DisplayType): Promise<DisplayType> {
    try {
      const insertData = {
        base_display_type_id: data.id, // Use the provided id as base_display_type_id
        name: data.name,
        description: data.description ?? null,
        size: data.size ?? null,
        category: data.category,
        selling_price: data.selling_price ?? null,
      };

      const { data: newCustomType, error } = await supabase.from('custom_display_types').insert(insertData).select().single();

      if (error) {
        console.error('Error creating custom display type:', error);
        throw new Error(`Failed to create custom display type: ${error.message}`);
      }

      // Map to DisplayType format
      return {
        id: newCustomType.id,
        name: newCustomType.name,
        description: newCustomType.description,
        size: newCustomType.size,
        category: newCustomType.category,
        selling_price: newCustomType.selling_price,
        created_at: newCustomType.created_at,
        updated_at: newCustomType.updated_at,
      };
    } catch (error) {
      console.error('Error creating custom display type:', error);
      throw error;
    }
  },

  async update(id: string, data: Partial<DisplayType>): Promise<DisplayType> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.name !== undefined) updateData.name = data.name;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.size !== undefined) updateData.size = data.size;
      if (data.category !== undefined) updateData.category = data.category;
      if (data.selling_price !== undefined) updateData.selling_price = data.selling_price;

      const { data: updatedCustomType, error } = await supabase
        .from('custom_display_types')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating custom display type:', error);
        throw new Error(`Failed to update custom display type: ${error.message}`);
      }

      if (!updatedCustomType) {
        throw new Error('Custom display type not found');
      }

      // Map to DisplayType format
      return {
        id: updatedCustomType.id,
        name: updatedCustomType.name,
        description: updatedCustomType.description,
        size: updatedCustomType.size,
        category: updatedCustomType.category,
        selling_price: updatedCustomType.selling_price,
        created_at: updatedCustomType.created_at,
        updated_at: updatedCustomType.updated_at,
      };
    } catch (error) {
      console.error('Error updating custom display type:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('custom_display_types').delete().eq('id', id);

      if (error) {
        console.error('Error deleting custom display type:', error);
        throw new Error(`Failed to delete custom display type: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting custom display type:', error);
      throw error;
    }
  },
};

